/** The unit of work a Task wraps: a (possibly async) function whose resolved value is the task result. */
type TaskCallback = () => Promise<unknown> | unknown;

/** Handler invoked when a task's callback throws/rejects. */
type TaskErrorHandler = (e: unknown) => unknown;

/** Names of the events a TaskScheduler can emit. */
type SchedulerEvent = 'end' | 'taskCompleted' | 'taskFailed' | 'depleted';

/**
 * Internal use only.
 * Represents a task in the TaskScheduler's queue.
 */
class Task {
  // Index in the task queue, as an incremental counter
  #index: number = 0;
  // The function to be executed, we assume a promise
  #callback: TaskCallback | null = null;
  // If none passed, we simply log errors to console
  #errorHandler: TaskErrorHandler = console.error;

  // Holds the promise
  #resultPromise: Promise<unknown> | undefined = undefined;
  // Holds resolution function
  #resolve: ((value: unknown) => void) | null = null;
  // Holds rejection function
  #reject: ((reason?: unknown) => void) | null = null;
  // Holds the returned value of original callback
  #resultValue: unknown = undefined;

  /**
   * NOTE (pre-existing quirk kept as-is): TaskScheduler#addToQueue constructs
   * `new Task(asyncFunction, errorHandler)`, so the error handler actually
   * arrives in `globalIndex` — hence the loose typing here.
   */
  constructor(callback: TaskCallback, globalIndex?: any, errorHandler?: TaskErrorHandler) {
    this.#index = globalIndex;
    this.#callback = callback;
    if (errorHandler) this.#errorHandler = errorHandler;

    this.#resultPromise = new Promise((r,rj) => {
      this.#resolve = r;
      this.#reject = rj;
    });
  }

  /**
   * Returns original callback function
   */
  get cb(): TaskCallback | null {
    return this.#callback;
  }

  /**
   * The actual value of the callback, once it's resolved
   */
  get result(): unknown {
    return this.#resultValue;
  }

  /**
   * Returns the promise that will eventually resolve with the value of the callback,
   * or optionally reject.
   */
  get resultPromise(): Promise<unknown> | undefined {
    return this.#resultPromise;
  }

  /**
   * Manually rejects the promise
   * @param reason why the task is rejected (message or Error)
   */
  reject(reason: string | Error | any): void {
    // Promise executor runs synchronously in the constructor, so #reject is always set
    this.#reject!(reason);
  }

  /**
   * Executes the task, which can resolve or reject
   */
  async run(): Promise<any> {
    try {
      const r = await this.#callback!();
      this.#resultValue = r;
      this.#resolve!(r);
    } catch(e) {
      this.#errorHandler(e);
      this.#reject!(e);
    }
  }
}

/**
 * An arbitrary task scheduler that lets you process "up to N" functions at
 * the same time.
 *
 * Once a task completes, it will automatically queue the next one, so that it's
 * always at max capacity.
 * @example
 * const maxAtTheSameTime = 2;
 * const scheduler = new TaskScheduler(maxAtTheSameTime);
 *
 * scheduler.on('taskCompleted', (value) => console.log("Task completed with value", value));
 * scheduler.on('taskFailed', (error) => console.error("Task failed with error", error));
 * scheduler.on('end', () => console.log("All tasks completed!"));
 * scheduler.on('depleted', (processingTasks) => console.log("Queue depleted, but some are still processing", processingTasks));
 *
 * scheduler.addToQueue(() => new Promise((r) => setTimeout(() => r(1), 1000)));
 * scheduler.addToQueue(someFunction)
 *
 * const getContentCB = () => {
 *   return ContentService.getContent(botId, template, intent, undefined, channelId);
 * }
 *
 * let result;
 * try {
 *   // Result will be the result of 'ContentService#getContent'
 *   result = await scheduler.addToQueue(getContentCB);
 * } catch(e) {
 *   console.error("Failed to get content", e);
 * }
 *
 * // Perform cleanup
 * scheduler.destroy();
 */
export default class TaskScheduler {
  // Internal check for destroyed state
  #destroyed = false;

  #indexesUsed = 0;

  // The tasks that are waiting in line
  #queue: Task[] = [];
  // The tasks that are currently being processed
  #processing: Task[] = [];
  #counter = 0;

  /** Maximum number of tasks allowed to run concurrently. */
  cap: number;

  // Event handlers
  #handlers: Record<SchedulerEvent, Array<(...args: any[]) => void>> = {
    "end": [],
    "taskCompleted": [],
    "taskFailed": [],
    "depleted": [],
  }

  constructor(maxParallel: number = 1) {
    this.cap = maxParallel;
    this.#indexesUsed = 0;
  }

  /**
   * Set up an event handler
   * @param event which scheduler event to listen for
   * @param callback listener invoked with event-specific args (result/error/processing list)
   */
  on(event: SchedulerEvent, callback: (...args: any[]) => void): void {
    if (!this.#handlers[event]) {
      throw new Error('Invalid event');
    }

    this.#handlers[event].push(callback);
  }

  /**
   * Removes an event listener
   * @param event which scheduler event the listener was registered on
   * @param callback the exact listener reference to remove
   */
  off(event: SchedulerEvent, callback: (...args: any[]) => void): void {
    if (!this.#handlers[event]) return;

    this.#handlers[event] = this.#handlers[event].filter(cb => cb !== callback);
  }

  /**
   * Destroys the task manager.
   * Removes all event listeners, queue, and processing items.
   *
   * Should be used if you set up event handlers.
   */
  destroy(): void {
    this.#destroyed = true;

    for (const key in this.#handlers) {
      this.#handlers[key as SchedulerEvent] = [];
    }

    for (const task of this.#queue) {
      task.reject(new Error('Task manager destroyed'));
    }

    for (const task of this.#processing) {
      task.reject(new Error('Task manager destroyed'));
    }

    this.#queue = [];
    this.#processing = [];
  }

  /**
   * Check if we have capacity to process more tasks at the same time
   */
  get hasCapacity(): boolean {
    return this.#processing.length < this.cap;
  }

  /** Test helper: returns a callback that resolves a random job id after a random (<1s) delay. */
  createDummyQueueItem(): () => Promise<string> {
    const randomTimeout = Math.floor(Math.random() * 1000) + 1;
    const randomJobId = Math.random().toString(16).slice(2);

    return () =>
      new Promise((r) => {
        setTimeout(() => {
          console.log('[%s] Job completed: %s', randomTimeout, randomJobId);
          return r(randomJobId);
        }, randomTimeout);
      });
  }

  /**
   * Add the task to the queue
   * @param asyncFunction The function/async function to be executed
   * @param errorHandler An optional handler if this specific `promiseFunction` throws an error
   * @returns Returns a promise that will resolve with the callback value (T) or reject
   */
  addToQueue<T = unknown>(asyncFunction: () => Promise<T> | T, errorHandler?: ((e: Error) => Promise<any> | any) | null): Promise<T> {
    if (this.#destroyed) throw new Error("Cannot add tasks to destroyed manager destroyed");

    const task = new Task(asyncFunction, errorHandler);
    this.#queue.push(task);

    // If there's room for more, process it right away
    if (this.hasCapacity) {
      this.#next();
    }

    // Gives you back a promise that will eventually
    // reject or resolve the value when the task is processed
    return task.resultPromise as Promise<T>;
  }

  /**
   * Triggers processing of the next task in the queue.
   */
  #next(): void {
    if (this.#destroyed) return;

    // Get the next Task to process
    const task = this.#queue.shift();

    // No more tasks...
    if (!task) {
      // The queue is depleted, AND all are processed.
      if (!this.#processing.length) {
        return this.#handlers.end.forEach(cb => cb());
      }

      // Queue depleted, but more are still processing
      return this.#handlers.depleted.forEach(cb => cb(this.#processing));
    }

    // Add the task itself to the list of processing tasks
    this.#processing.push(task);

    // ... then execute its task, and once it's resolved / rejected...
    task
      .run()
      .then(() => {
        /**
         * To obtain the value, you should already have received a promise
         * from the `addToQueue` handler. The promise will return the
         * value of the callback.
         *
         * The value is also emitted to every event handler.
         */

        // When task resolves, emit to handlers
        this.#handlers.taskCompleted.forEach(cb => cb(task.result, task.cb));

        // Remove it from the list of tasks currently processing
        this.#processing = this.#processing.filter(task => task !== task);

        // Then check if there are other tasks that can takt this one's place
        this.#next();
      })
      .catch(e => {
        /**
         * The promise you received from `addToQueue` will reject,
         * so you get the error there too.
         *
         * The error is also emitted to every event handler.
         */

        // Emit to every callback
        this.#handlers.taskFailed.forEach(cb => cb(e, task.cb));

        // The show must go on;
        // remove the task from the processing list and have another one take its place
        this.#processing = this.#processing.filter(task => task !== task);
        this.#next();
      });
  }
}
